import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { ReportSubstantiation } from '../../../../api';
import {
  CountryRuleEnum,
  DateReferenceEnum,
  ReportStatus,
  RetentionRuleType,
  RuleTypeEnum,
  useCountriesQuery,
  useCreateRetentionRuleMutation,
  useGetOrganisationCategoriesQuery,
  useRelationshipTypeQuery,
  useUpdateRetentionRuleMutation,
  useUserCountriesQuery,
} from '../../../../graphql/generated';
import { useIsFeatureEnabled } from '../../../../hooks';
import { FeatureFlag } from '../../../../types';
import { DropdownOption } from '../../molecules/Dropdown';
import {
  ConditionType,
  RetentionMode,
  RetentionRuleConditions,
  RuleDrawerVariant,
} from '../../utils/types';

export interface FormValues {
  selectedCountry: string | number;
  selectedRuleType: string | number;
  addToManualReview: boolean;
  applyToAllCases: boolean;
  selectedCondition: string | number;
  selectedConditionValue: string | number;
  mode: RetentionMode | null;
  periodValue: number | string;
  periodUnit: string;
  anchorDate: string;
  actionMode: RuleTypeEnum | null;
}

interface UseAddRuleDrawerProps {
  onClose: () => void;
  rule?: RetentionRuleType | null;
  variant: RuleDrawerVariant;
  country?: string;
  isOpen: boolean;
}

export const useAddRuleDrawer = ({
  onClose,
  rule,
  variant,
  country,
  isOpen,
}: UseAddRuleDrawerProps) => {
  const queryClient = useQueryClient();
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    reset,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      selectedCountry: '',
      selectedRuleType: '',
      addToManualReview: false,
      applyToAllCases: false,
      selectedCondition: '',
      selectedConditionValue: '',
      mode: null,
      periodValue: '',
      periodUnit: '',
      anchorDate: '',
      actionMode: null,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const mode = watch('mode');
  const selectedRuleType = watch('selectedRuleType');
  const selectedCountry = watch('selectedCountry');
  const selectedCondition = watch('selectedCondition');
  const selectedConditionValue = watch('selectedConditionValue');
  const applyToAllCases = watch('applyToAllCases');
  const periodValue = watch('periodValue');
  const periodUnit = watch('periodUnit');
  const anchorDate = watch('anchorDate');
  const actionMode = watch('actionMode');

  const { data: countriesData, isLoading: isLoadingCountries } =
    useCountriesQuery();
  const { data: userCountriesData, isLoading: isLoadingUserCountries } =
    useUserCountriesQuery();
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useGetOrganisationCategoriesQuery();
  const { data: relationshipTypesData, isLoading: isLoadingRTC } =
    useRelationshipTypeQuery();

  const { mutate: createRetentionRule, isLoading: isCreating } =
    useCreateRetentionRuleMutation({
      onSuccess: () => {
        queryClient.invalidateQueries('GetRetentionRules');
        queryClient.invalidateQueries('GetRulesByStatus');
        queryClient.invalidateQueries('GetFallbackRetentionRules');
        queryClient.invalidateQueries('GetUpcomingRetentionActionsCount');
        queryClient.invalidateQueries('GetRetentionRulesCount');
        reset();
        onClose();
      },
      onError: (error) => {
        console.error('Failed to create retention rule:', error);
      },
    });

  const { mutate: updateRetentionRule, isLoading: isUpdating } =
    useUpdateRetentionRuleMutation({
      onSuccess: () => {
        queryClient.invalidateQueries('GetRetentionRules');
        queryClient.invalidateQueries('GetRulesByStatus');
        queryClient.invalidateQueries('GetFallbackRetentionRules');
        queryClient.invalidateQueries('GetUpcomingRetentionActionsCount');
        queryClient.invalidateQueries('GetRetentionRulesCount');
        reset();
        onClose();
      },
      onError: (error) => {
        console.error('Failed to update retention rule:', error);
      },
    });

  const countriesAndRegionsEnabled = useIsFeatureEnabled(
    FeatureFlag.COUNTRIES_AND_REGIONS,
  );

  const ruleTypeOptions: DropdownOption[] = useMemo(() => {
    const baseOptions = [
      { value: CountryRuleEnum.Reporter, label: 'Reporter Country' },
      { value: CountryRuleEnum.Offender, label: 'Offender Country' },
    ];

    if (countriesAndRegionsEnabled) {
      return [
        ...baseOptions,
        { value: CountryRuleEnum.Case, label: 'Case Country' },
      ];
    }

    return baseOptions;
  }, [countriesAndRegionsEnabled]);

  const countryOptions: DropdownOption[] = useMemo(() => {
    const isUserCountryRule =
      selectedRuleType === CountryRuleEnum.Reporter ||
      selectedRuleType === CountryRuleEnum.Offender;

    if (isUserCountryRule) {
      return (
        userCountriesData?.organisation?.userCountries
          ?.filter((country) => country)
          .map((country) => ({
            value: country,
            label: country,
          })) ?? []
      );
    }
    return (
      countriesData?.countries.map((country) => ({
        value: country.code,
        label: country.name,
      })) ?? []
    );
  }, [selectedRuleType, countriesData, userCountriesData]);

  const isLoadingDropdown =
    selectedRuleType === CountryRuleEnum.Reporter ||
    selectedRuleType === CountryRuleEnum.Offender
      ? isLoadingUserCountries
      : isLoadingCountries;

  const formatEnumLabel = (value: string): string => {
    return value
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const allConditionOptions = useMemo(
    () => ({
      [ConditionType.CASE_STATUS]: Object.values(ReportStatus).map(
        (status) => ({
          value: status,
          label: formatEnumLabel(status),
        }),
      ),
      [ConditionType.SUBSTANTIATION]: Object.values(ReportSubstantiation).map(
        (sub) => ({
          value: sub,
          label: formatEnumLabel(sub),
        }),
      ),
      [ConditionType.CASE_CATEGORY]:
        categoriesData?.getOrganisationCategories?.map((category) => ({
          value: category.id,
          label:
            category.locales?.['en-GB']?.name ||
            category.locales?.['en']?.name ||
            `Category ${category.id}`,
        })) ?? [],
      [ConditionType.RELATIONSHIP_TO_COMPANY]:
        relationshipTypesData?.getRelationshipType?.map((rt) => ({
          value: rt.id,
          label: rt.name,
        })) ?? [],
    }),
    [categoriesData, relationshipTypesData],
  );

  const conditionValueOptions: DropdownOption[] = useMemo(() => {
    if (!selectedCondition) return [];
    return allConditionOptions[selectedCondition as ConditionType] ?? [];
  }, [allConditionOptions, selectedCondition]);

  const isLoadingConditionValues = useMemo(() => {
    if (!selectedCondition) return false;

    const loadingStates: Record<ConditionType, boolean> = {
      [ConditionType.CASE_STATUS]: false,
      [ConditionType.SUBSTANTIATION]: false,
      [ConditionType.CASE_CATEGORY]: isLoadingCategories,
      [ConditionType.RELATIONSHIP_TO_COMPANY]: isLoadingRTC,
    };

    return loadingStates[selectedCondition as ConditionType] ?? false;
  }, [selectedCondition, isLoadingCategories, isLoadingRTC]);

  useEffect(() => {
    if (applyToAllCases) {
      setValue('selectedCondition', '');
      setValue('selectedConditionValue', '');
    } else if (!selectedCondition) {
      setValue('selectedConditionValue', '');
    }
  }, [applyToAllCases, selectedCondition, setValue]);

  useEffect(() => {
    if (
      (variant === RuleDrawerVariant.EDIT ||
        variant === RuleDrawerVariant.EDITFALLBACK) &&
      rule
    ) {
      const dateReferenceMap: Record<DateReferenceEnum, string> = {
        [DateReferenceEnum.CaseClosure]: 'closure',
        [DateReferenceEnum.CaseSubmission]: 'submission',
        [DateReferenceEnum.CaseCreation]: 'creation',
        [DateReferenceEnum.CaseUnsubstantiation]: 'unsubstantiation',
        [DateReferenceEnum.CaseLastUpdate]: 'lastUpdate',
        [DateReferenceEnum.Immediate]: 'immediate',
      };

      const conditions = rule.conditions as RetentionRuleConditions;

      const hasRetentionPeriod = conditions.duration && conditions.duration > 0;

      const unitTitleCase = conditions.unit
        ? conditions.unit.charAt(0).toUpperCase() + conditions.unit.slice(1)
        : '';

      const addToManualReview = rule.requiresManualApproval ?? false;

      const isUserCountryRule =
        rule.countryRule === CountryRuleEnum.Reporter ||
        rule.countryRule === CountryRuleEnum.Offender;

      let countryValue = '';
      if (country) {
        if (isUserCountryRule) {
          countryValue = country;
        } else {
          countryValue =
            countriesData?.countries.find((c) => c.name === country)?.code ||
            '';
        }
      }

      reset({
        selectedCountry: countryValue,
        selectedRuleType: rule.countryRule || '',
        addToManualReview,
        applyToAllCases: conditions.applyToAll ?? false,
        selectedCondition: conditions.condition || '',
        selectedConditionValue: conditions.conditionValue ?? '',
        mode: hasRetentionPeriod
          ? RetentionMode.AFTER_PERIOD
          : RetentionMode.IMMEDIATE,
        periodValue: conditions.duration || '',
        periodUnit: unitTitleCase,
        anchorDate: dateReferenceMap[rule.dateReference] || 'creation',
        actionMode: rule.ruleType ?? null,
      });
    } else if (
      variant === RuleDrawerVariant.NEW ||
      variant === RuleDrawerVariant.FALLBACK
    ) {
      reset({
        selectedCountry: '',
        selectedRuleType: '',
        addToManualReview: false,
        applyToAllCases: false,
        selectedCondition: '',
        selectedConditionValue: '',
        mode: null,
        periodValue: '',
        periodUnit: '',
        anchorDate: '',
        actionMode: null,
      });
      setHasAttemptedSubmit(false);
    }
  }, [variant, rule, country, reset, countriesData?.countries]);

  const isFallback =
    variant === RuleDrawerVariant.FALLBACK ||
    variant === RuleDrawerVariant.EDITFALLBACK;

  const onSubmit = (data: FormValues) => {
    const conditions: Record<string, unknown> = {
      duration: 0,
      unit: 'days',
      applyToAll: data.applyToAllCases,
    };

    if (data.selectedCondition) {
      conditions.condition = data.selectedCondition;
    }

    if (data.selectedConditionValue && !data.applyToAllCases) {
      conditions.conditionValue = data.selectedConditionValue;
    }

    if (
      data.mode === RetentionMode.AFTER_PERIOD &&
      data.periodValue &&
      data.periodUnit
    ) {
      conditions.duration = Number(data.periodValue);
      conditions.unit = data.periodUnit.toLowerCase();
    }
    let dateReference: DateReferenceEnum;

    if (data.mode === RetentionMode.IMMEDIATE) {
      dateReference = DateReferenceEnum.Immediate;
    } else {
      const dateReferenceMap: Record<string, DateReferenceEnum> = {
        closure: DateReferenceEnum.CaseClosure,
        submission: DateReferenceEnum.CaseSubmission,
        creation: DateReferenceEnum.CaseCreation,
        unsubstantiation: DateReferenceEnum.CaseUnsubstantiation,
        lastUpdate: DateReferenceEnum.CaseLastUpdate,
      };

      dateReference =
        dateReferenceMap[data.anchorDate as string] ||
        DateReferenceEnum.CaseCreation;
    }

    if (
      (variant === RuleDrawerVariant.EDIT ||
        variant === RuleDrawerVariant.EDITFALLBACK) &&
      rule
    ) {
      const input = {
        id: rule.id,
        country: (data.selectedCountry as string) || null,
        countryRule: (data.selectedRuleType as CountryRuleEnum) || null,
        conditions,
        dateReference,
        ruleType: data.actionMode as RuleTypeEnum,
        requiresManualApproval: data.addToManualReview,
        ruleApproved: true,
      };
      updateRetentionRule({ input });
    } else {
      const parameters = {
        country: (data.selectedCountry as string) || null,
        countryRule: (data.selectedRuleType as CountryRuleEnum) || null,
        conditions,
        dateReference,
        ruleType: data.actionMode as RuleTypeEnum,
        requiresManualApproval: data.addToManualReview,
        ruleApproved: true,
        fallbackRule: isFallback,
      };
      createRetentionRule({ parameters });
    }
  };

  const baseValid = !!(
    mode &&
    actionMode &&
    (mode === RetentionMode.IMMEDIATE ||
      (mode === RetentionMode.AFTER_PERIOD &&
        periodValue &&
        periodUnit &&
        anchorDate)) &&
    (applyToAllCases || (selectedCondition && selectedConditionValue))
  );

  const isFormValid = isFallback
    ? baseValid
    : baseValid && !!selectedCountry && !!selectedRuleType;

  const showCountryError =
    !isFallback && hasAttemptedSubmit && !selectedCountry;
  const showRuleTypeError =
    !isFallback && hasAttemptedSubmit && !selectedRuleType;

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);
    handleFormSubmit(onSubmit)();
  };

  const handleClose = () => {
    setHasAttemptedSubmit(false);
    onClose();
  };

  return {
    control,
    handleSubmit,
    handleClose,
    ruleTypeOptions,
    countryOptions,
    isLoadingDropdown,
    isFormValid,
    isCreating: isCreating || isUpdating,
    setValue,
    periodValue,
    periodUnit,
    anchorDate,
    conditionValueOptions,
    isLoadingConditionValues,
    selectedCondition,
    applyToAllCases,
    showCountryError,
    showRuleTypeError,
  };
};
